import React from 'react';
import filter from "../../filter";
import {executeQuery} from "../../requests/executeQuery";
import shallowequal from "shallowequal";
import {calculateTotalColumn, renderPrintFooter} from "../../utils";
import {PROPERTIES_BY_BUYER_STATE} from '../../consts/queries';
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {chartDefaultColors} from "../../chartDefaultColors";
import {buyerStateChartOptions} from "./chartOptions";

const totalIndex = 1;
const cloneDeep = require('lodash.clonedeep');

export default class BuyerStateBreakdown extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            query: {},
            table: {
                header: ['Buyer State', '# of Properties', '%'],
                rows: [],
                total: ['Total', null, '100%'],
                criteria: [],
            },
            compareToTable: {
                header: ['Buyer State', '# of Properties', '%'],
                rows: [],
                total: ['Total', null, '100%'],
                criteria: [],
            },
            buyerStateChartOptions: cloneDeep(buyerStateChartOptions),
            isPrint: false,
            printBackground: '',
        };

        this.beforePrint = this.beforePrint.bind(this);
        this.afterPrint = this.afterPrint.bind(this);
    }

    componentDidMount() {
        this.setState({printBackground: `url('${arc.path.media+'Transaction_Background.png'}')`});
        this.fetch(this.props.location.query);
        window.history.replaceState(null, '');
        window.onbeforeprint = this.beforePrint;
        window.onafterprint = this.afterPrint;
    }

    componentDidUpdate(prevProps) {
        if (!shallowequal(prevProps.location.query, this.props.location.query)) {
            this.fetch(this.props.location.query);
        }
    }

    beforePrint() {
        this.setState({isPrint: true});
    }

    afterPrint() {
        this.setState({isPrint: false});
    }

    fetch(q) {
        const query = {...q};
        query.order = ['TOTAL_PROPERTIES.desc', 'COMPANY_STATE.asc'];
        const firstQuery = {...query};
        const secondQuery = {...query};

        let options = {}, optionsYearToCompare = {};
        if (query.COMPARISON_MIN || query.COMPARISON_MAX) {
            delete firstQuery.COMPARISON_MIN;
            delete firstQuery.COMPARISON_MAX;
            delete secondQuery.BASE_MIN;
            delete secondQuery.BASE_MAX;
            options = filter(firstQuery, 'buyer-breakdown');
            optionsYearToCompare = filter(secondQuery, 'buyer-breakdown');
        } else {
            options = filter(query, 'buyer-breakdown');
        }

        const requests = [executeQuery({queryName: PROPERTIES_BY_BUYER_STATE, options: options})];
        if (query.COMPARISON_MIN || query.COMPARISON_MAX) {
            requests.push(executeQuery({queryName: PROPERTIES_BY_BUYER_STATE, options: optionsYearToCompare}))
        }

        Promise.all(requests).then(res => {
            if (res && res[0] && res[0].data && Array.isArray(res[0].data)) {
                const table = BuyerStateBreakdown.buildTable(this.state.table, res[0].data, firstQuery);
                if (res[1] && res[1].data && Array.isArray(res[1].data)) {
                    const compareToTable = BuyerStateBreakdown.buildTable(this.state.compareToTable, res[1].data, secondQuery);
                    this.setState({table, compareToTable, query});
                    return;
                }
                this.setState({table, query});
                return;
            }
            this.setState({query});
        });
    }

    static parseKey(key) {
        let k = key;
        if (k.includes('_MIN')) {
            k = k.replace('_MIN', '');
        }
        if (k.includes('_MAX')) {
            k = k.replace('_MAX', '');
        }
        k = k.split('_').join(' ');
        return k[0] + k.slice(1).toLowerCase();
    }

    static parseValue(value) {
        return value[0].toUpperCase() + value.slice(1).toLowerCase();
    }

    static parseCriteria(q) {
        const query = {...q};
        const criteria = [];
        delete query.order;

        Object.keys(query).map(key => {
            if (key.includes('MIN')) {
                const c = [query[key]];
                const k = key.replace('_MIN', '_MAX');
                if (query[k]) {
                    c.push(query[k]);
                } else {
                    c.push('today');
                }
                const value = c.join(' - ');
                if (['BASE_MIN', 'COMPARISON_MIN'].includes(key)) {
                    criteria.unshift(`${BuyerStateBreakdown.parseKey(key)}: ${value}`);
                    return;
                }
                criteria.push(`${BuyerStateBreakdown.parseKey(key)}: ${value}`);
                return;
            }
            if (key.includes('MAX')) {
                const k = key.replace('_MAX', '_MIN');
                if (query[k]) {
                    return;
                }
            }
            if (['BASE_MAX', 'COMPARISON_MAX'].includes(key)) {
                criteria.unshift(`${BuyerStateBreakdown.parseKey(key)}: ${query[key]}`);
                return;
            }
            let value = query[key];
            if (['CATEGORY'].includes(key)) {
                value = BuyerStateBreakdown.parseValue(value);
            }
            criteria.push(`${BuyerStateBreakdown.parseKey(key)}: ${value}`);
        });
        return criteria;
    }

    static buildTable(state, data, query) {
        const table = {...state};
        table.rows = data;
        table.total[totalIndex] = calculateTotalColumn(table.rows, 'TOTAL_PROPERTIES');
        table.rows.map(row => {
            const percentage = row.TOTAL_PROPERTIES / table.total[totalIndex] * 100;
            row.PERCENTAGE = percentage ? percentage.toFixed(2) : 0;
        });
        table.criteria = BuyerStateBreakdown.parseCriteria(query);
        return table;
    }

    static renderTable(table) {
        return (
            <div className='table-container'>
                <table className='table'>
                    <thead>
                    <tr>
                        {
                            table.header.map((col, index) => {
                                return <th key={index} className='th'>{col}</th>
                            })
                        }
                    </tr>
                    </thead>
                    <tbody>
                    {
                        table.rows.map((row, index) => {
                            return (
                                <tr key={index}>
                                    {
                                        Object.keys(row).map((key, index) => {
                                            const hasPercent = key === 'PERCENTAGE';
                                            return <td key={`${key}-${index}`} className='td'>{row[key]}{hasPercent ? '%' : ''}</td>
                                        })
                                    }
                                </tr>
                            )
                        })
                    }
                    </tbody>
                    <tfoot>
                    <tr>
                        {
                            table.total.map((col, index) => {
                                return <td key={index} className='td total'>{col}</td>
                            })
                        }
                    </tr>
                    </tfoot>
                </table>
                <div className='criteria'>
                    {
                        table.criteria.map((criteria, index) => {
                            const comma = index !== table.criteria.length - 1 ? ',' : null;
                            return <div key={index}>{criteria}{comma}</div>
                        })
                    }
                </div>
            </div>
        );
    }

    static renderPieChart(table, buyerStateChartOptions) {
        let index = 0, otherPercentage = 0, otherCount = 0;
        const chartOptions = cloneDeep(buyerStateChartOptions);

        if (table.rows.length) {
            table.rows.forEach((row, rowIndex) => {
                if (rowIndex < 3) {
                    chartOptions.series[0].data.push({
                        name: row.COMPANY_STATE,
                        y: parseInt(row.TOTAL_PROPERTIES),
                        color: chartDefaultColors[index++]
                    });
                } else {
                    otherPercentage += parseFloat(row.PERCENTAGE);
                    otherCount += parseInt(row.TOTAL_PROPERTIES);
                }
            });

            if (otherPercentage && otherCount) {
                chartOptions.series[0].data.push({
                    name: 'Other',
                    y: otherCount,
                    color: chartDefaultColors[index]
                });
            }
        }

        if (chartOptions.series[0].data.length) {
            return <HighchartsReact highcharts={Highcharts} options={chartOptions}/>
        }
        return <div>No results</div>
    }

    static renderData(table, buyerStateChartOptions, year, isPrint) {
        return (
            <div className='breakdown-container'>
                <h3 className='title'>Buyer State Breakdown - {year}</h3>
                <div className='breakdown-data'>
                    {BuyerStateBreakdown.renderTable(table)}
                    <div className='chart-container' style={{width: isPrint ? '220px' : '250px'}} key={Math.random()}>
                        {BuyerStateBreakdown.renderPieChart(table, buyerStateChartOptions)}
                    </div>
                </div>
            </div>
        )
    }

    render() {
        const {table, compareToTable, buyerStateChartOptions, query, isPrint, printBackground} = this.state;
        const year = query && query.BASE_MIN ? new Date(query.BASE_MIN).getFullYear() : null;
        const comparisonYear = query && query.COMPARISON_MIN ? new Date(query.COMPARISON_MIN).getFullYear() : null;
        delete query.order;

        return (
            <div className='BuyerStateBreakdown' style={{backgroundImage: isPrint && printBackground}}>
                {BuyerStateBreakdown.renderData(table, buyerStateChartOptions, year, isPrint)}
                {
                    comparisonYear ? BuyerStateBreakdown.renderData(compareToTable, buyerStateChartOptions, comparisonYear, isPrint) : null
                }
                {renderPrintFooter(true)}
                <div className='preload-print-background' style={{backgroundImage: printBackground}}/>
            </div>
        )
    }
}
